// The shapes a draft travels in: the create screen's state <-> `PUT /api/ticket-drafts/:id`
// (docs/API.md §12.3) and `GET /api/ticket-drafts/:id` (§12.4).
//
// The screen's state is `{ caller, fields, composer, touched }`:
//   caller    `{ organization, location, customer }`, each a row object or null. Only their
//             ids are saved (they become columns); a reopened draft gets back the `{ id, name }`
//             rows the server joined for labels.
//   fields    New.jsx's `fields` slice.
//   composer  New.jsx's `composer` slice. Its `subject` is saved as the draft's own `subject`
//             column (so a tab label needs no JSON parsing) and is NOT repeated in the payload.
//   touched   the keys among DEFAULTABLE the agent chose themselves. Each child in
//             `fields.children` carries its own `touched` the same way (`childTouched`).
//
// ABSENT VERSUS NULL. A form, queue or owner the agent never touched is left OUT of what is
// saved, so it resolves to the default of the day the draft is FILED, not the day it was
// opened; an explicit `null` owner means "unassigned" and is kept. The server's
// creator-is-default-owner rule turns on that difference (docs/API.md §3.3, §12.3).

export const PAYLOAD_VERSION = 1;

/**
 * What the server allows a draft to hold: the payload's JSON plus the subject, in UTF-8
 * bytes (src/services/ticket-drafts.js, `MAX_PAYLOAD_BYTES`; docs/API.md §12.3).
 */
export const MAX_DRAFT_BYTES = 262144;

/** The size of a PUT body by the server's measure, so an over-size one is caught before it is sent. */
export function draftBytes(body) {
  const encoder = new TextEncoder();
  return encoder.encode(JSON.stringify(body?.payload ?? null)).length
    + encoder.encode(typeof body?.subject === 'string' ? body.subject : '').length;
}

/** The fields that have a default the server (or the config) supplies when they are absent. */
export const DEFAULTABLE = ['form_id', 'queue_id', 'assigned_user_id'];

/** `touched` arrives as an array (it is stored as JSON) or a Set (it is handy as state). */
function touchedSet(touched) {
  return new Set(touched ?? []);
}

/**
 * What a draft id looks like: a GUID, either case. The same pattern as the server's
 * `isDraftId` (src/services/ticket-drafts.js), which answers 404 to anything else; a test
 * holds the two equal (test/web/draft-model.test.js).
 */
export const DRAFT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True for a string the server would accept as a draft id. */
export function isDraftId(value) {
  return typeof value === 'string' && DRAFT_ID_PATTERN.test(value);
}

/**
 * A new draft's id, minted by the browser (docs/API.md §12.1): the draft exists before the
 * server has seen it, and its first save creates it under this id.
 *
 * `crypto.randomUUID` needs a secure context; the fallback builds a v4 GUID from
 * `getRandomValues`, which does not, so an intranet address over plain http still works.
 */
export function newDraftId(crypto = globalThis.crypto) {
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // RFC 4122 variant
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Removes the DEFAULTABLE keys of `body` that are not in `touched`. Works on any object that
 * carries them: a `POST /api/tickets` body at filing, the `fields` slice at save time.
 * Everything else, and an explicit `null` for a touched key, is left alone. Returns a copy.
 */
export function omitUntouched(body, touched) {
  const keep = touchedSet(touched);
  const out = { ...body };
  for (const key of DEFAULTABLE) {
    if (!keep.has(key)) delete out[key];
  }
  return out;
}

/**
 * The DEFAULTABLE keys a child ticket's agent chose. A child gets its own list when it is
 * added (child-tickets.js `newChildDraft`, from the parent's). A child saved before children
 * had one counts as having chosen ALL of them, so nothing already on it is lost.
 */
export function childTouched(child) {
  return Array.isArray(child?.touched) ? [...child.touched] : [...DEFAULTABLE];
}

/** Each child as saved: its untouched form, queue and owner left out, its `touched` kept. */
function savedChildren(children) {
  return children.map((child) => {
    const touched = childTouched(child);
    return { ...child, fields: omitUntouched(child?.fields ?? {}, touched), touched };
  });
}

/** The PUT body for the screen's state, to be applied on top of `baseRevision`. */
export function toSaveBody({ caller, fields, composer, touched } = {}, baseRevision = 0) {
  const { subject, ...composerRest } = composer ?? {};
  const savedFields = omitUntouched(fields ?? {}, touched);
  if (Array.isArray(savedFields.children)) savedFields.children = savedChildren(savedFields.children);
  return {
    base_revision: baseRevision,
    organization_id: caller?.organization?.id ?? null,
    location_id: caller?.location?.id ?? null,
    customer_id: caller?.customer?.id ?? null,
    // '' (an input the agent cleared) is saved as null: "no subject yet".
    subject: typeof subject === 'string' && subject !== '' ? subject : null,
    payload: {
      v: PAYLOAD_VERSION,
      fields: savedFields,
      composer: composerRest,
      touched: [...touchedSet(touched)],
    },
  };
}

/** A caller slot from a draft row: the joined `{ id, name }` row, or just the id if the join is missing. */
function callerSlot(row, id) {
  if (row) return row;
  return id ? { id } : null;
}

/**
 * The screen's state from a `GET /api/ticket-drafts/:id` row. Missing pieces come back as
 * empty slices, so a draft saved by an older client (or with no payload at all) still opens.
 * `touched` comes back as an array.
 */
export function fromDraft(draft = {}) {
  const payload = draft.payload ?? {};
  return {
    caller: {
      organization: callerSlot(draft.organization, draft.organization_id),
      location: callerSlot(draft.location, draft.location_id),
      customer: callerSlot(draft.customer, draft.customer_id),
    },
    fields: { ...(payload.fields ?? {}) },
    composer: { ...(payload.composer ?? {}), subject: draft.subject ?? '' },
    touched: [...touchedSet(payload.touched)],
  };
}
